import React from "react";
import clsx from "clsx";

interface ConsentSettingRowProps {
  title: React.ReactNode;
  helpText?: React.ReactNode;
  // Pills or tooltips shown inline after the title.
  adornment?: React.ReactNode;
  // "split" puts the label in a left column; "stacked" gives the control the full width (tables).
  layout?: "split" | "stacked";
  id?: string;
  children?: React.ReactNode;
}

export const ConsentSettingRow = ({
  title,
  helpText,
  adornment,
  layout = "split",
  id,
  children,
}: ConsentSettingRowProps) => (
  <div
    id={id}
    className={clsx(
      "grid gap-x-[32px] gap-y-[12px] border-0 border-t border-solid border-neutral-200 py-[20px]",
      layout === "split" ? "grid-cols-[216px_minmax(0,1fr)]" : "grid-cols-1",
    )}
  >
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-[8px] text-[14px] font-medium leading-[20px] text-neutral-900">
        {title}
        {adornment}
      </div>
      {helpText && (
        <div
          className={clsx(
            "mt-[4px] text-[14px] leading-[20px] text-neutral-600",
            layout === "stacked" && "max-w-[64ch]",
          )}
        >
          {helpText}
        </div>
      )}
    </div>
    {children && <div className="flex min-w-0 flex-col items-start gap-[12px]">{children}</div>}
  </div>
);

/** A text-styled button for secondary actions like "Reset to default". */
export const ConsentLinkButton = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="cursor-pointer border-0 bg-transparent p-0 text-[14px] font-medium leading-[20px] text-primary-600 underline underline-offset-2 hover:text-primary-700"
  >
    {children}
  </button>
);

/** Small grey caption under a control, e.g. a character count. */
export const ConsentCaption = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[12px] leading-[16px] text-neutral-600">{children}</span>
);
